import React, { useEffect, useState } from 'react';
import { Modal, Button, Alert, Spinner } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { usePaywall } from './PaywallProvider.js';
import { startAnnualCheckout } from './billingActions.js';
import './paywall.scss';

export default function JoinCircusModal() {
  const navigate = useNavigate();
  const { joinModalOpen, closeJoinModal, access, refreshAccess } = usePaywall();
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [checkoutError, setCheckoutError] = useState(null);

  const trialDaysRemaining = access?.trialDaysRemaining ?? null;

  useEffect(() => {
    if (!joinModalOpen) {
      setCheckoutError(null);
      setCheckoutLoading(false);
      return undefined;
    }

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [joinModalOpen]);

  const goToLearnMore = () => {
    closeJoinModal();
    navigate('/learn-more');
  };

  const handleJoinNow = async () => {
    setCheckoutError(null);
    setCheckoutLoading(true);
    try {
      const result = await startAnnualCheckout({ intent: 'join' });
      if (result.redirecting) {
        return;
      }

      await refreshAccess();
      closeJoinModal();
      if (result.alreadyActive) {
        navigate('/sectionHome');
      } else {
        navigate('/learn-more', { state: { checkoutSuccess: true } });
      }
    } catch (error) {
      if (error.code === 'EMAIL_VERIFICATION_REQUIRED') {
        closeJoinModal();
        navigate('/viewProfile');
        return;
      }
      if (error.code === 'STUB_DISABLED' || error.code === 'STRIPE_NOT_CONFIGURED') {
        goToLearnMore();
        return;
      }
      setCheckoutError(error.message || 'Checkout failed. Please try again.');
    } finally {
      setCheckoutLoading(false);
    }
  };

  return (
    <Modal
      show={joinModalOpen}
      onHide={closeJoinModal}
      centered
      className="paywall-join-modal"
    >
      <Modal.Header closeButton>
        <Modal.Title>Join the Circus</Modal.Title>
      </Modal.Header>
      <Modal.Body className="text-center">
        <p className="mb-4">
          Keep full access to every skill, review cards, and progress tracking after your trial ends.
        </p>
        <Button
          variant="warning"
          size="lg"
          className="paywall-join-modal__cta mb-3"
          onClick={handleJoinNow}
          disabled={checkoutLoading}
        >
          {checkoutLoading ? (
            <>
              <Spinner animation="border" size="sm" className="me-2" />
              Processing…
            </>
          ) : (
            'Join Now!'
          )}
        </Button>
        <p className="paywall-join-modal__price mb-3">$30 for one-year, full access</p>
        <button
          type="button"
          className="btn btn-link paywall-join-modal__learn-more"
          onClick={goToLearnMore}
          disabled={checkoutLoading}
        >
          Learn More
        </button>
        {checkoutError && (
          <Alert variant="danger" className="mt-3 mb-0 text-start">
            {checkoutError}
          </Alert>
        )}
        {trialDaysRemaining != null && (
          <p className="paywall-join-modal__trial-footer text-muted small mb-0 mt-3">
            {trialDaysRemaining} {trialDaysRemaining === 1 ? 'day' : 'days'} remaining in your free trial
          </p>
        )}
      </Modal.Body>
    </Modal>
  );
}
